// Tour registry. A tour is { key, title, description, steps[] } (schema in
// TutorialFlow.jsx). Every project gets the core "What is Cortex?" tour;
// projects may add their own tours — either statically here (PROJECT_TOURS)
// or at runtime via registerProjectTours() (e.g. from an app or a plugin),
// which is the extension point for project-specific tutorials.
import { coreTour } from "./core";
import { flowTour } from "./flow";
import { agenticSourcesTour } from "./agenticSources";
import { modelsTour } from "./models";
import { organizationsTour } from "./organizations";
import { DEMO_TOURS } from "./demoProjects";
import { potentialCustomersTour } from "./potentialCustomers";

const PROJECT_TOURS = {
  ...DEMO_TOURS,
  potential_customers: [potentialCustomersTour],
};

// Runtime-registered tours (slug → [tours]); merged after the static ones.
const DYNAMIC_TOURS = {};

export function registerProjectTours(slug, tours, { replaceGuides = false } = {}) {
  if (!slug || !Array.isArray(tours)) return;
  // Agent-generated GUIDES (tour.__guide set) re-register on every refresh —
  // replace the previous guide set instead of appending duplicates.
  const kept = replaceGuides
    ? (DYNAMIC_TOURS[slug] || []).filter((t) => !t.__guide)
    : (DYNAMIC_TOURS[slug] || []);
  DYNAMIC_TOURS[slug] = [...kept, ...tours];
}

// The generic cortex product tours, in narrative order: the governance side,
// the AI-built-sources feature, the tenancy/policies layer, the engine side.
const CORTEX_TOURS = [coreTour, agenticSourcesTour, modelsTour, organizationsTour, flowTour];

// Grouped view for the chooser: CORTEX tutorials first (the product itself),
// then the active project's own tutorials underneath, visually separated.
export function getTourGroupsForProject(slug) {
  const projectTours = [...(PROJECT_TOURS[slug] || []), ...(DYNAMIC_TOURS[slug] || [])];
  const groups = [{ key: "cortex", label: "Cortex tutorials", tours: CORTEX_TOURS }];
  if (projectTours.length) groups.push({ key: "project", label: "This project", tours: projectTours });
  return groups;
}

// Flat list (same order as the groups) — used for key lookups and the
// single-tour shortcut.
export function getToursForProject(slug) {
  return getTourGroupsForProject(slug).flatMap((g) => g.tours);
}

export { coreTour, flowTour, agenticSourcesTour, organizationsTour };
